/**
 * Attaches the signed-in user's Cognito ID token to every request sent to the dashboard APIs
 * (both the primary Lambda Function URL and the Campaign Scheduler API Gateway).
 *
 * Patches window.fetch once, so all existing `fetch(...)` calls are covered
 * without touching each component. Requests to any other host are left untouched.
 *
 * The token goes in `X-Afima-Auth` for the Lambda Function URL (and `Authorization: Bearer`
 * for API Gateway `fh8trgf2w0`, whose CORS config explicitly allows `Authorization`).
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_BASE_URL, ACCOUNTING_API_BASE_URL, SMS_SCHEDULER_API_URL } from './config';

const CAMPAIGN_SCHEDULER_API_URL = 'https://fh8trgf2w0.execute-api.eu-west-1.amazonaws.com';

const API_PREFIXES = Array.from(
  new Set([API_BASE_URL, ACCOUNTING_API_BASE_URL, CAMPAIGN_SCHEDULER_API_URL, SMS_SCHEDULER_API_URL])
).filter((p) => p.startsWith('https://'));

const requestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

const getIdToken = async (): Promise<string | undefined> => {
  try {
    const session = await fetchAuthSession(); // refreshes an expired token automatically
    return session.tokens?.idToken?.toString();
  } catch {
    return undefined; // not signed in -> send the request without a token
  }
};

const w = window as Window & { __afimaAuthFetchInstalled?: boolean };

if (!w.__afimaAuthFetchInstalled) {
  w.__afimaAuthFetchInstalled = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = requestUrl(input);
    if (!API_PREFIXES.some((p) => url.startsWith(p))) {
      return originalFetch(input, init);
    }
    const token = await getIdToken();
    if (!token) {
      return originalFetch(input, init);
    }
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has('X-Afima-Auth')) headers.set('X-Afima-Auth', token);
    if (url.startsWith(CAMPAIGN_SCHEDULER_API_URL) && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return originalFetch(input, { ...init, headers });
  };
}

export {};
